import type { UserAvatarProps } from './types';
import { ReactComponent as DialInPhoneIcon } from 'Assets/icons/hangup_v2.svg';
import groupChatIcon from 'Assets/images/group-chat.svg';
import phoneIcon from 'Assets/images/phone-icon-sidebar.svg';
import cx from 'classnames';
import * as React from 'react';
import { Popup } from 'semantic-ui-react';
import { formatUserInitials } from 'Utils/formatUserInitials';
import { Styled } from './index.styles';

export const UserAvatar: React.FC<UserAvatarProps> = ({
  conversationId = '0',
  presence,
  showUnread,
  image,
  name,
  conversationGrouping,
  hideInitials,
  isInactive,
  isPhone,
  className,
  size,
  fontSize,
  iconSize,
}) => {
  const [imageError, setImageError] = React.useState(false);

  const handleImageError = () => {
    setImageError(true);
  };

  const getUserPresenceTooltipLabel = (): string | undefined => {
    if (isInactive) return 'Unknown';

    if (!presence?.state) return '';

    const presenceState = presence.state.toLowerCase();
    if (presence.state === 'DoNotDisturb') {
      return 'Do Not Disturb';
    }
    if (presence.state === 'OnCall') {
      return 'On Call';
    }
    return presenceState.charAt(0).toUpperCase() + presenceState.slice(1);
  };

  const avatarClassName = `list-item-icon-wrapper ${className || ''}`;

  const renderFallbackAvatar = (src: string) => (
    <Styled.Avatar
      className={avatarClassName}
      $size={size}
      $fontSize={fontSize}
    >
      <Styled.GroupImage src={src} $size={iconSize} />
    </Styled.Avatar>
  );

  const getAvatar = () => {
    if (isPhone) {
      return (
        <Styled.Avatar
          className={avatarClassName}
          $size={size}
          $fontSize={fontSize}
          data-testid="user-avatar-dial-in"
        >
          <DialInPhoneIcon
            aria-hidden
            className="size-[18px] text-background"
          />
        </Styled.Avatar>
      );
    }

    if (!(name || image)) {
      const isGroupOrChannel =
        conversationGrouping === 'Channel' ||
        conversationGrouping === 'Group';

      return renderFallbackAvatar(
        isGroupOrChannel ? groupChatIcon : phoneIcon
      );
    }

    const getPresenceState = () => {
      if (!presence?.state) {
        return undefined;
      }

      if (isInactive) {
        return 'Unknown';
      }

      return presence?.state;
    };

    return (
      <Styled.Avatar
        $hideInitials={hideInitials}
        $size={size}
        $fontSize={fontSize}
        $isInactive={isInactive}
        className={avatarClassName}
      >
        {!hideInitials && (
          <>
            {image && !imageError ? (
              <Styled.ImageWrapper>
                <Styled.Image
                  src={image}
                  alt={name || 'profile picture'}
                  onError={handleImageError}
                />
              </Styled.ImageWrapper>
            ) : (
              <Styled.Initials className="credentials">
                {formatUserInitials(name)}
              </Styled.Initials>
            )}
          </>
        )}
        <Styled.UserPresence
          $hideInitials={hideInitials}
          state={getPresenceState()}
          conversationId={conversationId}
          showUnread={showUnread}
        />
      </Styled.Avatar>
    );
  };

  return (
    <Popup
      trigger={getAvatar()}
      content={getUserPresenceTooltipLabel()}
      position="top right"
      className={cx('status-popup', {
        'hide-popup': !presence,
      })}
    />
  );
};
